import { useRouter } from 'next/router';
import first from 'lodash/first';
import useBasketPrices from '@components/modules/Basket/BasketStatic/useBasketPrices';
import BASKET_PAYMENT_MODES from '@constants/basketPaymentModes';
import DIET_LENGTH_MODE from '@constants/dietLengtMode';
import useDietTimeSlots from '@hooks/basket/useDietTimeSlots';
import useSelectDaysByRange from '@hooks/newOrder/useSelectDaysByRange';
import {
  selectModuleConfigClientPanel,
  useAppConfigSelector,
  useAppMode,
} from '@hooks/useAppConfigSelectors';
import useBlockDelivery from '@hooks/useBlockDelivery';
import { selectOrderForm, useOrderFormSelector } from '@hooks/useOrderForm';
import { getIDfromIRI } from '@services/Api.service';
import { transformNumberPaymentModeToBasketFormat } from '@services/Basket.service';
import { format, formatArray } from '@services/Date.service';
import { getInitialOfferSelection } from '@services/OrderForm.serviceTS';

import useDefaultPaymentMode from './useDefaultPaymentMode';
import useWeekInclude from './useWeekInclude';

const useDefaultDietElement = () => {
  const { isOnlyShop } = useAppMode();
  if (isOnlyShop) {
    return {};
  }

  const {
    customDietLengthEnabled,
    days: { standardDays = {} } = {},
    diets = [],
    predefinedDietLengthEnabled,
    packages,
    packageLineFilters = [],
    // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  } = useOrderFormSelector(selectOrderForm);

  const {
    deliveryOptions: {
      pickUpPoint: isPickUpPointEnabled,
      address: isAddressEnabled,
    } = {},
    // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  } = useAppConfigSelector(selectModuleConfigClientPanel);

  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  const { getBlockDays } = useBlockDelivery();

  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  const weekInclude = useWeekInclude();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  const defaultPaymentModeNumber = useDefaultPaymentMode();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  const [getDietTimeSlots] = useDietTimeSlots();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  const [selectDaysByRange] = useSelectDaysByRange();

  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  const { query } = useRouter();

  const {
    diet: firstDiet,
    variant: firstVariant,
    calorific: firstCalorific,
    offerPackage,
  } = getInitialOfferSelection(diets, packages);

  const dietTimeSlots = getDietTimeSlots(getIDfromIRI(firstDiet?.['@id']));

  const defaultPaymentMode = transformNumberPaymentModeToBasketFormat(
    defaultPaymentModeNumber
  );
  const isSubscription =
    defaultPaymentMode === BASKET_PAYMENT_MODES.SUBSCRIPTION_PAYMENT;
  // eslint-disable-next-line radix -- FIXME
  const queryDietLength = parseInt(query.dietLength);

  // eslint-disable-next-line no-nested-ternary -- FIXME
  const dietLength = isSubscription
    ? getBlockDays(new Date(dietTimeSlots?.firstPossibleOrderDay)).length
    : predefinedDietLengthEnabled && !customDietLengthEnabled
    ? (standardDays?.options ?? [])?.[0]
    : (queryDietLength || standardDays?.default) ?? 5;

  const newDeliveryDates = formatArray(
    selectDaysByRange(
      new Date(dietTimeSlots?.firstPossibleOrderDay),
      dietLength,
      {
        disabledDays: dietTimeSlots?.disabledDays,
        disabledDaysOfWeek: dietTimeSlots?.disabledDaysOfWeek,
      }
    )
  );

  const dietLineFilter = packageLineFilters.find(filter =>
    filter.activePackagesIds.includes(offerPackage?.id)
  );

  const values = {
    id: firstDiet?.id,
    addons: [],
    address: {},
    calorificId: firstCalorific?.['@id'],
    calorificName: firstCalorific?.name,
    deliveryDates: newDeliveryDates,
    // eslint-disable-next-line no-nested-ternary -- FIXME
    deliveryType: isAddressEnabled
      ? 'ADDRESS'
      : isPickUpPointEnabled
      ? 'PICKUP_POINT'
      : null,
    dietLength,
    firstDeliveryDay:
      first(newDeliveryDates) ??
      format(new Date(dietTimeSlots?.firstPossibleOrderDay)),
    optionChangeMenu: false,
    packageId: offerPackage?.['@id'] ?? null,
    packageLineFilters: dietLineFilter,
    paymentMode: defaultPaymentMode,
    pickUpPoint: {},
    useEcoContainers: false,
    dietId: firstDiet?.['@id'],
    variantId: firstVariant?.['@id'],
    ...(firstVariant?.superVariantBeta
      ? {
          mealTypes: (firstVariant?.mealTypes || []).map(
            mealType => mealType?.['@id']
          ),
        }
      : {}),
    variantName: firstVariant?.name,
    dietLengthMode:
      predefinedDietLengthEnabled && !customDietLengthEnabled
        ? DIET_LENGTH_MODE.PREDEFINED
        : DIET_LENGTH_MODE.CUSTOM,
    ...weekInclude,
    allowStepWithAdditionalMealTypes:
      firstDiet?.allowStepWithAdditionalMealTypes,
    hasDietsToSelectMenu: firstDiet?.hasDietsToSelectMenu,
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME
  const basketPrices = useBasketPrices({
    dietElements: [values],
  });

  return { ...values, price: basketPrices.price };
};

export default useDefaultDietElement;
